/**
 * Componente principal do WebChat Widget - Versão Premium
 * Com dark mode automático e todas as integrações
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FloatingButton } from './components/FloatingButton';
import { ChatWindow } from './components/ChatWindow';
import { useVisitorSession } from './hooks/useVisitorSession';
import { useWidgetConfig } from './hooks/useWidgetConfig';
import { useMessageQueue } from './hooks/useMessageQueue';
import { useMessageHistory } from './hooks/useMessageHistory';
import { useWebchatConnection } from './hooks/useWebchatConnection';
// Hook para screen share WebRTC (lado do cliente)
import { useClientScreenShare } from '@/hooks/webrtc';
import type { WidgetInitProps, InteractiveButton } from './types';
import { createLogger } from '@/lib/logger';

// Logger para diagnóstico
const logger = createLogger('Widget');

interface WidgetProps {
  config: WidgetInitProps;
}

export const Widget: React.FC<WidgetProps> = ({ config }) => {
  const { inboxId, visitor: initialVisitor, visitorId: providedVisitorId } = config;
  
  // Estados do widget
  const [isOpen, setIsOpen] = useState(false);
  const [showPreChatForm, setShowPreChatForm] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [historyInitialized, setHistoryInitialized] = useState(false);
  // Detecta se é viewport mobile para ocultar FAB quando chat aberto
  const [isMobile, setIsMobile] = useState(false);
  
  // Ref para controlar timeout do indicador de digitação
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hooks
  // Passa providedVisitorId para garantir consistência no modo autenticado (interno)
  // Isso evita que o hook gere UUID anônimo quando já temos o user.id
  const { visitor, isLoading: isLoadingVisitor, identify } = useVisitorSession(inboxId, providedVisitorId);
  const { config: widgetConfig, isLoading: isLoadingConfig, error: widgetConfigError } = useWidgetConfig(inboxId);
  
  // Determina visitor_id efetivo
  // Prioridade: providedVisitorId (seguro, vindo do auth) > sessionVisitor (localStorage)
  const effectiveVisitorId = providedVisitorId || visitor?.visitor_id || null;
  
  const { 
    messages, 
    isSending, 
    conversationId, 
    addMessage, 
    sendMessage, 
    sendButtonResponse,
    sendFile,
    retryMessage,
    initializeFromHistory,
    prependMessages,
    clearMessages,
  } = useMessageQueue({ inboxId, visitor });
  
  // Ref para estabilizar clearMessages e evitar condição de corrida
  // Deve ser criado após useMessageQueue que define clearMessages
  const clearMessagesRef = useRef(clearMessages);
  clearMessagesRef.current = clearMessages;

  // Hook para carregar histórico de mensagens
  const {
    messages: historyMessages,
    conversationId: historyConversationId,
    isLoading: isLoadingHistory,
    error: historyError,
    hasMore: historyHasMore,
    loadMore: loadMoreHistory,
    refetch: refetchHistory,
    hasStarted: historyHasStarted,  // NOVO: flag que indica se fetch já iniciou
  } = useMessageHistory({
    visitorId: effectiveVisitorId,
    inboxId,
    // CORRIGIDO: enabled só depende de ter visitorId válido
    // historyInitialized controla apenas o merge, não o start do fetch
    enabled: !!effectiveVisitorId && !isLoadingConfig && !widgetConfigError,
  });

  // ===== CONVERSATION ID EFETIVO PARA SCREEN SHARE =====
  // Prioridade: conversationId do messageQueue > historyConversationId
  // Isso garante que o signaling conecte ao canal correto mesmo antes do merge
  const effectiveConversationId = conversationId || historyConversationId;

  // Reset de estado quando muda inboxId ou visitorId
  // Essencial para troca de tenant ou logout/login
  // CORRIGIDO: Usar ref para evitar que clearMessages recriado dispare o efeito
  useEffect(() => {
    logger.debug('Reset de estado: inboxId ou visitorId mudou');
    setHistoryInitialized(false);
    clearMessagesRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxId, effectiveVisitorId]);

  // Inicializa/merge mensagens do histórico quando carregado
  // Agora suporta "corrida" (usuário envia antes do histórico terminar)
  useEffect(() => {
    // Só processa se ainda não foi marcado como inicializado
    if (historyInitialized) return;
    
    // CORRIGIDO: Só marca como inicializado se o fetch JÁ INICIOU e JÁ TERMINOU
    // Evita marcar true antes do useEffect do useMessageHistory "engatar"
    if (!historyHasStarted) {
      // Fetch ainda não iniciou - aguardar
      return;
    }
    
    if (isLoadingHistory) {
      // Fetch em andamento - aguardar conclusão
      return;
    }
    
    // Fetch concluiu (hasStarted=true, isLoading=false)
    if (historyMessages.length === 0) {
      // Histórico realmente vazio - marca inicializado
      logger.debug('Histórico vazio após fetch - marcando inicializado');
      setHistoryInitialized(true);
      return;
    }
    
    // Histórico veio com mensagens - fazer merge
    if (messages.length === 0) {
      // Caso normal: histórico chegou antes de qualquer mensagem local
      logger.debug('Inicializando histórico', { count: historyMessages.length });
      initializeFromHistory(historyMessages, historyConversationId);
    } else {
      // Caso "corrida": usuário já enviou mensagem antes do histórico terminar
      // Mescla histórico com mensagens locais (histórico vem antes)
      logger.debug('Merge de histórico com mensagens locais (corrida detectada)');
      prependMessages(historyMessages);
    }
    setHistoryInitialized(true);
  }, [historyMessages, historyConversationId, messages.length, historyInitialized, isLoadingHistory, historyHasStarted, initializeFromHistory, prependMessages]);

  // Detecta preferência de dark mode do sistema
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);
    
    const handler = (e: MediaQueryListEvent) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handler);
    
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Detecta viewport mobile para ocultar FAB quando chat aberto
  // Threshold de 480px é padrão para smartphones em portrait
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 480);
    checkMobile(); // Verifica no mount
    
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handler para evento de typing do agente/bot
  const handleAgentTyping = useCallback((isTyping: boolean) => {
    // Limpa timeout anterior se existir
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    
    setIsAgentTyping(isTyping);
    
    // Auto-reset após 5 segundos se não receber mais eventos
    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        setIsAgentTyping(false);
      }, 5000);
    }
  }, []);

  // Conexão Realtime
  const handleNewMessage = useCallback((message: any) => {
    // Só adiciona mensagens outbound (do agente) via Realtime
    if (message.direction === 'outbound') {
      addMessage(message);
      
      // Incrementa contador se chat fechado
      if (!isOpen) {
        setUnreadCount((prev) => prev + 1);
        
        // Notificação sonora (se suportado)
        if ('Notification' in window && Notification.permission === 'granted') {
          // Som sutil de notificação
          try {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.value = 800;
            oscillator.type = 'sine';
            gainNode.gain.value = 0.1;
            
            oscillator.start();
            oscillator.stop(audioContext.currentTime + 0.1);
          } catch (e) {
            // Ignora erro de áudio
          }
        }
      }
      
      // Para indicador de digitação
      setIsAgentTyping(false);
    }
  }, [addMessage, isOpen]);

  const { isConnected } = useWebchatConnection({
    conversationId,
    // CORRIGIDO: Usar effectiveVisitorId para consistência entre envio, realtime e histórico
    // Isso evita "split brain" onde partes do sistema usam IDs diferentes
    visitorId: effectiveVisitorId,
    inboxId: !isLoadingConfig && !widgetConfigError ? config.inboxId || null : null,
    onNewMessage: handleNewMessage,
    onTyping: handleAgentTyping,
  });

  // ===== HOOK WEBRTC SCREEN SHARE (LADO CLIENTE) =====
  // Permite que o agente visualize a tela do cliente durante suporte
  // Usa effectiveConversationId para conectar ao canal mesmo durante carregamento do histórico
  const clientScreenShare = useClientScreenShare({
    conversationId: effectiveConversationId,
    enabled: isConnected && !!effectiveConversationId,
  });

  // Identifica visitante inicial se fornecido
  useEffect(() => {
    if (initialVisitor && visitor && !visitor.name) {
      identify(initialVisitor.name, initialVisitor.email, initialVisitor.phone);
      setShowPreChatForm(false);
    }
  }, [initialVisitor, visitor, identify]);

  // Auto-open delay
  useEffect(() => {
    if (widgetConfig?.auto_open_delay && !isOpen) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, widgetConfig.auto_open_delay * 1000);
      
      return () => clearTimeout(timer);
    }
  }, [widgetConfig?.auto_open_delay, isOpen]);

  // Determina se deve mostrar formulário pré-chat
  // Só mostra se pelo menos um campo é obrigatório
  useEffect(() => {
    const hasRequiredFields = widgetConfig?.require_name || widgetConfig?.require_email || widgetConfig?.require_phone;
    
    if (visitor?.name) {
      setShowPreChatForm(false);
    } else if (!hasRequiredFields) {
      // Se nenhum campo é obrigatório, pula o formulário
      setShowPreChatForm(false);
    } else if (messages.length > 0) {
      setShowPreChatForm(false);
    }
  }, [visitor, widgetConfig, messages]);

  // Listener para evento de identificação externa
  useEffect(() => {
    const handleIdentify = (e: CustomEvent) => {
      const { name, email, phone } = e.detail || {};
      if (name || email || phone) {
        identify(name, email, phone);
        setShowPreChatForm(false);
      }
    };
    
    window.addEventListener('uaizychat:identify', handleIdentify as EventListener);
    return () => window.removeEventListener('uaizychat:identify', handleIdentify as EventListener);
  }, [identify]);

  // Toggle do widget
  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  // Submit do formulário pré-chat
  const handlePreChatSubmit = useCallback((name: string, email: string, phone: string) => {
    identify(name, email, phone);
    setShowPreChatForm(false);
  }, [identify]);

  // Envio de mensagem
  const handleSendMessage = useCallback(async (content: string) => {
    await sendMessage(content);
  }, [sendMessage]);

  // Envio de arquivo (5.4)
  const handleSendFile = useCallback(async (file: File) => {
    await sendFile(file);
  }, [sendFile]);

  // Retry de mensagem
  const handleRetryMessage = useCallback(async (messageId: string) => {
    await retryMessage(messageId);
  }, [retryMessage]);

  // Clique em botão interativo do Flow Engine
  const handleButtonClick = useCallback(async (button: InteractiveButton) => {
    // Para botões do tipo URL, abre em nova aba
    if (button.type === 'url' && button.url) {
      window.open(button.url, '_blank', 'noopener,noreferrer');
      return;
    }
    // Para botões reply, envia resposta ao webhook
    await sendButtonResponse(button.id, button.text);
  }, [sendButtonResponse]);

  // Loading state
  if (isLoadingVisitor || isLoadingConfig || !widgetConfig) {
    return null;
  }

  // Se a inbox configurada estiver inativa/inexistente, o widget não deve
  // continuar carregando histórico ou realtime. Isso evita 404/403 recorrentes
  // no preview enquanto a configuração administrativa não é corrigida.
  if (widgetConfigError) {
    return null;
  }

  return (
    <>
      {/* Reset de estilos para isolar o widget */}
      <style>
        {`
          #uaizychat-widget-container * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
          #uaizychat-widget-container input,
          #uaizychat-widget-container textarea,
          #uaizychat-widget-container button {
            font-family: inherit;
          }
        `}
      </style>

      {/* CSS customizado do tenant */}
      {widgetConfig.custom_css && (
        <style>{widgetConfig.custom_css}</style>
      )}

      {/* Botão flutuante */}
      <FloatingButton
        onClick={handleToggle}
        primaryColor={widgetConfig.primary_color}
        position={widgetConfig.position}
        unreadCount={unreadCount}
        isOpen={isOpen}
        isDarkMode={isDarkMode}
        // Props para modo border-logo
        buttonStyle={widgetConfig.button_style}
        buttonBorderWidth={widgetConfig.button_border_width}
        companyLogoUrl={widgetConfig.header_logo_url}
        // Oculta FAB no mobile quando chat aberto (evita obstruir botão enviar)
        hideOnMobileOpen={isMobile}
        // Permite minimizar em mini-pill (configurável por inbox)
        allowMinimize={widgetConfig.allow_minimize ?? false}
        inboxId={inboxId}
      />

      {/* Janela do chat */}
      <ChatWindow
        isOpen={isOpen}
        messages={messages}
        onSendMessage={handleSendMessage}
        onSendFile={handleSendFile}
        onRetryMessage={handleRetryMessage}
        onClose={handleToggle}
        primaryColor={widgetConfig.primary_color}
        position={widgetConfig.position}
        greetingMessage={widgetConfig.greeting_message}
        requireName={widgetConfig.require_name ?? true}
        requireEmail={widgetConfig.require_email}
        requirePhone={widgetConfig.require_phone ?? false}
        showBranding={widgetConfig.show_branding}
        isSending={isSending}
        isConnected={isConnected}
        showPreChatForm={showPreChatForm}
        onPreChatSubmit={handlePreChatSubmit}
        isAgentTyping={isAgentTyping}
        companyName={widgetConfig.company_name || "Suporte"}
        // Novas props para avatar dinâmico no header
        headerAvatarMode={widgetConfig.header_avatar_mode}
        headerLogoUrl={widgetConfig.header_logo_url}
        onButtonClick={handleButtonClick}
        enableFileUpload={true}
        hasMoreMessages={historyHasMore}
        onLoadMoreMessages={loadMoreHistory}
        isLoadingHistory={isLoadingHistory}
        historyError={historyError}
        onRetryHistory={() => {
          // Handler de retry: reset estado para permitir novo merge
          setHistoryInitialized(false);
          refetchHistory();
        }}
        // ===== WEBRTC SCREEN SHARE =====
        screenShareState={clientScreenShare}
      />
    </>
  );
};
